import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { GuardianBadge } from "@/components/access_studio/GuardianBadge";
import { RuleRow } from "@/components/access_studio/RuleRow";
import { FiveSectionDetail } from "@/components/access_studio/FiveSectionDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform/access-studio`;
const PLATFORM_API = `${process.env.REACT_APP_BACKEND_URL}/api/platform`;

const TABS = [
  ["master-list", "Master List"],
  ["by-role", "By Role"],
  ["by-page", "By Page"],
  ["guardian", "Guardian"],
  ["coverage", "Coverage"],
];

const STATUS_LABEL = { menu: "menu", "url-only": "url-only", blocked: "blocked" };

function exportCoverage(map, format) {
  const roles = map?.meta?.roles || [];
  const rows = (map?.by_page || []).map((page) => {
    const record = { route: page.path };
    page.roles.forEach((role) => {
      record[role.role] = role.status;
    });
    return record;
  });
  let content;
  let mime;
  let ext;
  if (format === "json") {
    content = JSON.stringify({ generated_at: new Date().toISOString(), roles: roles.map((r) => r.role), rows }, null, 2);
    mime = "application/json";
    ext = "json";
  } else {
    const header = ["route", ...roles.map((r) => r.role)];
    const lines = [header.join(",")];
    rows.forEach((record) => {
      lines.push([record.route, ...roles.map((r) => record[r.role] || "blocked")].join(","));
    });
    content = lines.join("\n");
    mime = "text/csv";
    ext = "csv";
  }
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `jfm-guard-coverage.${ext}`;
  anchor.click();
  URL.revokeObjectURL(url);
}

const tabButtonStyle = (active) => ({
  background: active ? "#111827" : "transparent",
  color: active ? "#fff" : "#374151",
  border: "1px solid",
  borderColor: active ? "#111827" : "#d1d5db",
  borderRadius: "999px",
  cursor: "pointer",
  fontSize: "0.82rem",
  fontWeight: 500,
  padding: "0.3rem 0.85rem",
});

export default function PlatformAccessStudioPage() {
  const [innerTab, setInnerTab] = useState("master-list");
  const [map, setMap] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRole, setSelectedRole] = useState("supervisor");
  const [selectedPage, setSelectedPage] = useState(null);
  const [reverifying, setReverifying] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [simulatedOff, setSimulatedOff] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      if (simulatedOff.length) params.simulate_off = simulatedOff.join(",");
      const [mapResponse, guardianResponse] = await Promise.all([
        axios.get(`${API}/map`, { params }),
        axios.get(`${API}/guardian`),
      ]);
      setMap(mapResponse.data);
      setGuardian(guardianResponse.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Access map could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [companyId, simulatedOff]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSimulated = (key) =>
    setSimulatedOff((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));

  useEffect(() => {
    axios
      .get(`${PLATFORM_API}/companies`)
      .then((response) => setCompanies(response.data || []))
      .catch(() => setCompanies([]));
  }, []);

  const rerunGuardian = async () => {
    setReverifying(true);
    try {
      const response = await axios.get(`${API}/guardian/verify`);
      setGuardian(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Re-verification failed.");
    } finally {
      setReverifying(false);
    }
  };

  if (loading && !map) {
    return (
      <section data-testid="access-studio-loading" style={{ padding: "1rem" }}>
        Loading the JFM truth map…
      </section>
    );
  }

  return (
    <section data-testid="platform-access-studio-page">
      <div
        data-testid="access-studio-scope-banner"
        style={{
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          borderRadius: "8px",
          padding: "0.6rem 0.85rem",
          marginBottom: "0.85rem",
          fontSize: "0.82rem",
          color: "#1e3a8a",
        }}
      >
        <strong>Read-only inspector.</strong> {map?.meta?.scope}. Changes nobody's access.
        <div data-testid="access-studio-native-note" style={{ marginTop: "0.3rem", color: "#475569" }}>
          {map?.meta?.native_app}
        </div>
        <div data-testid="access-studio-web-status" style={{ marginTop: "0.3rem" }}>
          Web verification: <GuardianBadge status={map?.meta?.web_status === "verified" ? "verified" : "not_verified"} />{" "}
          <small>{map?.meta?.web_detail}</small>
        </div>
        <div style={{ marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <label htmlFor="access-studio-company" style={{ fontSize: "0.78rem", fontWeight: 600 }}>
            Company overlay:
          </label>
          <select
            id="access-studio-company"
            data-testid="access-studio-company-picker"
            onChange={(event) => setCompanyId(event.target.value)}
            value={companyId}
            style={{ padding: "0.25rem 0.5rem", borderRadius: "6px", border: "1px solid #bfdbfe", fontSize: "0.78rem" }}
          >
            <option value="">Global blueprint (all features on)</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        {map?.company_overlay && (
          <span data-testid="access-studio-overlay-summary" style={{ display: "block", marginTop: "0.35rem", fontSize: "0.75rem", color: "#475569" }}>
            {map.company_overlay.name} · {map.company_overlay.status}
          </span>
        )}
        </div>
        <div data-testid="access-studio-feature-simulator" style={{ marginTop: "0.45rem" }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#334155" }}>
            Feature simulation (tap to toggle){map?.meta?.simulated ? " — SIMULATION ONLY · live JFM unchanged" : ""}:
          </span>
          <div style={{ marginTop: "0.3rem", display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
            {Object.entries(map?.meta?.effective_features || {}).map(([key, on]) => (
              <button
                key={key}
                type="button"
                data-testid={`access-studio-feature-toggle-${key}`}
                onClick={() => toggleSimulated(key)}
                style={{
                  fontSize: "0.7rem",
                  padding: "0.12rem 0.5rem",
                  borderRadius: "6px",
                  cursor: "pointer",
                  border: "1px solid",
                  borderColor: on ? "#86efac" : "#fca5a5",
                  background: on ? "#dcfce7" : "#fee2e2",
                  color: on ? "#166534" : "#991b1b",
                }}
              >
                {key}: {on ? "on" : "off"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <p data-testid="access-studio-error" role="alert" style={{ color: "#991b1b" }}>
          {error}
        </p>
      )}

      <nav
        aria-label="Access Studio views"
        data-testid="access-studio-tab-bar"
        style={{ display: "flex", gap: "0.4rem", marginBottom: "0.85rem", flexWrap: "wrap" }}
      >
        {TABS.map(([key, label]) => (
          <button
            key={key}
            data-testid={`access-studio-tab-${key}`}
            onClick={() => setInnerTab(key)}
            style={tabButtonStyle(innerTab === key)}
            type="button"
          >
            {label}
          </button>
        ))}
      </nav>

      {innerTab === "master-list" && (
        <div data-testid="access-studio-master-list">
          {(map?.master_list || []).map((row) => (
            <div
              key={row.path}
              data-testid={`access-studio-master-row-${row.path}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
                border: "1px solid #e2e5ea",
                borderRadius: "8px",
                padding: "0.5rem 0.75rem",
                marginBottom: "0.35rem",
                background: row.lifecycle === "redirect" ? "#f9fafb" : "#fff",
              }}
            >
              <div>
                <code style={{ fontSize: "0.82rem" }}>{row.path}</code>
                <small style={{ display: "block", color: "#6b7280" }}>
                  {row.component}
                  {row.feature ? ` · feature: ${row.feature}` : ""}
                  {row.redirect_to ? ` → ${row.redirect_to}` : ""}
                </small>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <span
                  style={{ fontSize: "0.7rem", color: "#374151", background: "#f3f4f6", borderRadius: "6px", padding: "0.1rem 0.45rem" }}
                >
                  {row.lifecycle}
                </span>
                {row.guardian.verified > 0 && <GuardianBadge status="verified" count={row.guardian.verified} />}
                {row.guardian.mismatch > 0 && <GuardianBadge status="mismatch" count={row.guardian.mismatch} />}
                {row.guardian.not_verified > 0 && <GuardianBadge status="not_verified" count={row.guardian.not_verified} />}
              </div>
            </div>
          ))}
        </div>
      )}

      {innerTab === "by-role" && (
        <div data-testid="access-studio-by-role">
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
            {(map?.meta?.roles || []).map((role) => (
              <button
                key={role.role}
                data-testid={`access-studio-role-pick-${role.role}`}
                onClick={() => setSelectedRole(role.role)}
                style={tabButtonStyle(selectedRole === role.role)}
                type="button"
              >
                {role.label}
              </button>
            ))}
          </div>
          <FiveSectionDetail sections={map?.by_role?.[selectedRole]} />
        </div>
      )}

      {innerTab === "by-page" && (
        <div data-testid="access-studio-by-page">
          <select
            data-testid="access-studio-page-picker"
            onChange={(event) => setSelectedPage(event.target.value)}
            value={selectedPage || ""}
            style={{ padding: "0.35rem 0.5rem", borderRadius: "6px", border: "1px solid #d1d5db", marginBottom: "0.75rem" }}
          >
            <option value="">Select a page…</option>
            {(map?.by_page || []).map((page) => (
              <option key={page.path} value={page.path}>
                {page.path}
              </option>
            ))}
          </select>
          {(map?.by_page || [])
            .filter((page) => page.path === selectedPage)
            .map((page) => (
              <div key={page.path} data-testid={`access-studio-page-detail-${page.path}`}>
                {page.roles.map((role) => (
                  <div
                    key={role.role}
                    data-testid={`access-studio-page-role-${role.role}`}
                    style={{ border: "1px solid #e2e5ea", borderRadius: "8px", padding: "0.5rem 0.75rem", marginBottom: "0.35rem" }}
                  >
                    <strong>{role.label}</strong>{" "}
                    <GuardianBadge status={role.status === "menu" ? "verified" : role.status === "url-only" ? "mismatch" : "not_verified"} />
                    <small style={{ display: "block", color: "#6b7280" }}>
                      {role.status === "menu"
                        ? "Shown in this role's menu"
                        : role.status === "url-only"
                          ? "Not in menu — reachable by direct URL (no page guard)"
                          : "Hidden / blocked for this role"}
                    </small>
                  </div>
                ))}
              </div>
            ))}
        </div>
      )}

      {innerTab === "guardian" && (
        <div data-testid="access-studio-guardian">
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
            <GuardianBadge status="verified" count={guardian?.counts?.verified || 0} />
            <GuardianBadge status="mismatch" count={guardian?.counts?.mismatch || 0} />
            <GuardianBadge status="not_verified" count={guardian?.counts?.not_verified || 0} />
            <button
              data-testid="access-studio-guardian-reverify"
              disabled={reverifying}
              onClick={rerunGuardian}
              type="button"
              style={{ border: "1px solid #d1d5db", background: "transparent", borderRadius: "6px", cursor: "pointer", fontSize: "0.78rem", padding: "0.25rem 0.7rem" }}
            >
              {reverifying ? "Re-verifying…" : "Re-verify (read-only)"}
            </button>
          </div>
          {(guardian?.checks || []).map((check, index) => (
            <RuleRow
              key={`${check.page}-${check.rule}-${index}`}
              label={`${check.page} · ${check.rule}`}
              status={check.status}
              manifestText={check.manifest}
              evidenceText={check.evidence}
            />
          ))}
        </div>
      )}

      {innerTab === "coverage" && (
        <div data-testid="access-studio-coverage">
          <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.6rem", flexWrap: "wrap" }}>
            <button
              data-testid="access-studio-coverage-export-csv"
              onClick={() => exportCoverage(map, "csv")}
              type="button"
              style={{ border: "1px solid #d1d5db", background: "transparent", borderRadius: "6px", cursor: "pointer", fontSize: "0.78rem", padding: "0.25rem 0.7rem" }}
            >
              Export CSV
            </button>
            <button
              data-testid="access-studio-coverage-export-json"
              onClick={() => exportCoverage(map, "json")}
              type="button"
              style={{ border: "1px solid #d1d5db", background: "transparent", borderRadius: "6px", cursor: "pointer", fontSize: "0.78rem", padding: "0.25rem 0.7rem" }}
            >
              Export JSON
            </button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="access-studio-coverage-table" style={{ borderCollapse: "collapse", fontSize: "0.75rem", width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "0.35rem 0.5rem", borderBottom: "2px solid #e2e5ea", position: "sticky", left: 0, background: "#fff" }}>Route</th>
                  {(map?.meta?.roles || []).map((role) => (
                    <th key={role.role} style={{ padding: "0.35rem 0.5rem", borderBottom: "2px solid #e2e5ea", whiteSpace: "nowrap" }}>
                      {role.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(map?.by_page || []).map((page) => (
                  <tr key={page.path} data-testid={`access-studio-coverage-row-${page.path}`}>
                    <td style={{ padding: "0.3rem 0.5rem", borderBottom: "1px solid #f1f5f9", fontFamily: "monospace", position: "sticky", left: 0, background: "#fff" }}>
                      {page.path}
                    </td>
                    {page.roles.map((role) => (
                      <td
                        key={role.role}
                        style={{
                          padding: "0.3rem 0.5rem",
                          borderBottom: "1px solid #f1f5f9",
                          textAlign: "center",
                          color: role.status === "menu" ? "#166534" : role.status === "url-only" ? "#b45309" : "#991b1b",
                          background: role.status === "menu" ? "#f0fdf4" : role.status === "url-only" ? "#fffbeb" : "#fef2f2",
                        }}
                      >
                        {STATUS_LABEL[role.status] || role.status}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
